import React from 'react';
import { Row, Col, Card, CardBody, Spinner } from 'reactstrap';
import KeyFigureStatic from '@/common/charts/KeyFigureStatic';
import { PlayerMetricsDto } from '@/services/analytics/analytics-types';

interface PlayerMetricsSectionProps {
  data?: PlayerMetricsDto;
  isLoading: boolean;
}

const PlayerMetricsSection: React.FC<PlayerMetricsSectionProps> = ({ data, isLoading }) => {
  if (isLoading) {
    return (
      <div className="p-5 text-center">
        <Spinner color="primary" />
        <p className="mt-2">Loading player metrics...</p>
      </div>
    );
  }

  if (!data) {
    return <div className="p-5 text-center">No data available</div>;
  }

  const createMetricItem = (
    value: number,
    label: string,
    change?: number,
    icon = 'bx bx-user',
    prefix = '',
    decimals = 0,
  ) => ({
    id: 1,
    cardColor: 'primary',
    label: label,
    badge: change && change > 0 ? 'ri-arrow-right-up-line' : change && change < 0 ? 'ri-arrow-right-down-line' : '',
    badgeClass: change && change > 0 ? 'success' : change && change < 0 ? 'danger' : '',
    percentage: change ? `${change > 0 ? '+' : ''}${change.toFixed(2)}%` : '',
    counter: value,
    bgcolor: 'secondary',
    icon,
    decimals,
    prefix,
    suffix: '',
    separator: ',',
    link: '', // Added missing link property
  });

  return (
    <>
      {/* Active Users Metrics */}
      <Row className="mb-3">
        <Col lg={12}>
          <h5 className="mb-3">Active Users</h5>
        </Col>
      </Row>
      <Row className="gy-3 mb-4">
        <KeyFigureStatic
          item={createMetricItem(data.dau, 'Daily Active Users (DAU)', data.dauChange, 'bx bx-user-check')}
        />
        <KeyFigureStatic
          item={createMetricItem(data.wau, 'Weekly Active Users (WAU)', data.wauChange, 'bx bx-user-check')}
        />
        <KeyFigureStatic
          item={createMetricItem(data.mau, 'Monthly Active Users (MAU)', data.mauChange, 'bx bx-user-check')}
        />
      </Row>

      {/* Growth Metrics */}
      <Row className="mb-3">
        <Col lg={12}>
          <h5 className="mb-3">Growth & Conversions</h5>
        </Col>
      </Row>
      <Row className="gy-3 mb-4">
        <KeyFigureStatic
          columnProps={{ md: 3, sm: 12 }}
          item={createMetricItem(data.newRegistrations, 'New Registrations', undefined, 'bx bx-user-plus')}
        />

        <KeyFigureStatic
          columnProps={{ md: 3, sm: 12 }}
          item={createMetricItem(data.kycVerified, 'KYC Verified', undefined, 'bx bx-check-shield')}
        />

        <KeyFigureStatic
          columnProps={{ md: 3, sm: 12 }}
          item={createMetricItem(
            data.firstDepositConversions,
            'First Deposits/Transfers',
            undefined,
            'bx bx-dollar-circle',
          )}
        />

        <KeyFigureStatic
          columnProps={{ md: 3, sm: 12 }}
          item={createMetricItem(data.churnRate, 'Churn Rate', undefined, 'bx bx-user-minus', '', 1)}
        />
      </Row>

      {/* Player Segments */}
      <Row className="mb-3">
        <Col lg={6}>
          <Card>
            <CardBody>
              <h5 className="card-title mb-3">
                Player Segments
                <i
                  className="bx bx-info-circle text-muted ms-2"
                  style={{ cursor: 'pointer' }}
                  title="Players are categorized based on their total deposit amounts during the selected period"
                ></i>
              </h5>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span title="Players with total deposits less than $1,000" style={{ cursor: 'help' }}>
                  Casual Players
                  <small className="text-muted ms-1">(&lt; $1,000)</small>
                </span>
                <span className="badge bg-info">{data.playerSegments.casual}</span>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span title="Players with total deposits between $1,000 - $9,999" style={{ cursor: 'help' }}>
                  Regular Players
                  <small className="text-muted ms-1">($1K - $10K)</small>
                </span>
                <span className="badge bg-primary">{data.playerSegments.regular}</span>
              </div>
              <div className="d-flex justify-content-between align-items-center">
                <span title="Players with total deposits of $10,000 or more" style={{ cursor: 'help' }}>
                  Whales
                  <small className="text-muted ms-1">($10K+)</small>
                </span>
                <span className="badge bg-success">{data.playerSegments.whales}</span>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col lg={6}>
          <Card>
            <CardBody>
              <h5 className="card-title mb-3">Top Countries</h5>
              <div className="table-responsive">
                <table className="table-sm mb-0 table">
                  <thead>
                    <tr>
                      <th>Country</th>
                      <th className="text-end">Players</th>
                      <th className="text-end">%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.geoDistribution || []).slice(0, 5).map((geo, index) => (
                      <tr key={index}>
                        <td>{geo.country}</td>
                        <td className="text-end">{geo.count.toLocaleString()}</td>
                        <td className="text-end">{geo.percentage.toFixed(2)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default PlayerMetricsSection;
